/**
 * Cross-reference parser for Australian/NZ standards.
 *
 * Detects standard references (AS/NZS 3000:2018, NCC 2025 Volume One, etc.)
 * and clause references (Clause 2.3.1, Section H6D2, Table 3.1 of AS 1428.1)
 * in text, splitting into segments for rendering as clickable chips.
 */

/* ── Sub-patterns ──────────────────────────────────────────── */

const STD      = '(?:AS\\/NZS|AS|NZS)';           // standard body prefix
const STD_NUM  = '\\d{3,5}(?:\\.\\d+)*';           // e.g. 3000, 3500.1, 1428.1
const YEAR     = '(?::\\d{4})?';                    // optional edition year :2018
const NCC      = '(?:NCC|BCA)';                     // National Construction Code / Building Code of Australia
const NCC_NUM  = '\\d{4}';                          // NCC edition year
const VOL      = '(?:\\s+Volume\\s+(?:One|Two|Three|\\d+))?'; // NCC volume

const CL_KW    = '(?:Clause|Section|Table|Part|Appendix|Schedule|Fig(?:ure)?)';
// Liberal clause identifier (used when already qualified by a standard prefix)
const CL_ID    = '[A-Z0-9][A-Za-z0-9]*(?:\\.[A-Za-z0-9]+)*';
// Strict clause identifier for standalone refs (needs dot OR letter+digit mix)
const CL_ID_S  = '(?:[A-Z0-9]+\\.[A-Za-z0-9.]+|[A-Z]\\d[A-Za-z0-9.]*|\\d+[A-Z][A-Za-z0-9.]*)';

const CL_REF   = `${CL_KW}\\s+${CL_ID}`;          // e.g. Clause 2.3.1
const CL_REF_S = `${CL_KW}\\s+${CL_ID_S}`;        // stricter standalone version

/* ── Combined pattern (order: longest match first) ─────────── */

const CROSSREF_SRC = [
  // 1. "Clause X.Y of AS/NZS NNNN(:YYYY)"
  `\\b${CL_REF}\\s+of\\s+${STD}\\s+${STD_NUM}${YEAR}`,
  // 2. "Clause X.Y of NCC/BCA YYYY (Volume ...)"
  `\\b${CL_REF}\\s+of\\s+${NCC}\\s+${NCC_NUM}${VOL}`,
  // 3. "AS/NZS NNNN(:YYYY) (Clause X.Y)?"
  `\\b${STD}\\s+${STD_NUM}${YEAR}(?:\\s+${CL_REF})?`,
  // 4. "NCC/BCA YYYY (Volume ...) (Clause X.Y)?"
  `\\b${NCC}\\s+${NCC_NUM}${VOL}(?:\\s+${CL_REF})?`,
  // 5. Standalone clause ref (strict — must have dot or letter+digit mix)
  `\\b${CL_REF_S}`,
].join('|');

/**
 * Regex for quick `.test()` (no `g` flag — safe for repeated calls).
 */
export const CROSSREF_TEST = new RegExp(CROSSREF_SRC);

/**
 * Splitting regex — single outer capture group so `.split()` interleaves
 * plain text (even indices) and matched references (odd indices).
 */
export const CROSSREF_SPLIT = new RegExp(`(${CROSSREF_SRC})`);

/**
 * Build a natural-language query from a matched cross-reference string.
 */
export function buildCrossRefQuery(match: string): string {
  return `Show me ${match}`;
}
